import { useCallback, useEffect } from "react";

import { resolveSmsChatName } from "@components/chat-dock";
import { emitChatNotification } from "src/notifications/notification-center/chatNotificationBus";
import { formatUSPhone } from "../../../utils";
import { useVirtualAssistant } from "../context/VirtualAssistantContext";
import { SmsMessageWithCompany, useSmsSocket } from "./hooks/useSmsSocket";

const getSmsBody = (sms: SmsMessageWithCompany): string => {
  return sms.content?.Body || "(no content)";
};

const getPhoneDisplay = (phone?: string): string => {
  if (!phone) return "Unknown number";

  const digits = phone.replace(/\D/g, "");

  if (digits.length === 11 && digits.startsWith("1")) {
    return `+1 ${formatUSPhone(digits.slice(1))}`;
  }

  if (digits.length === 10) {
    return formatUSPhone(digits);
  }

  return phone;
};

export function SmsNotification() {
  const { sessionNumber, companyNumbersData } = useVirtualAssistant();

  const handleIncomingSms = useCallback((sms: SmsMessageWithCompany) => {
    if (!sms.from) return;

    emitChatNotification({
      id: sms.uid,
      kind: "sms",
      companyId: sms.companyId ?? null,
      companyName: sms.companyName ?? null,
      title: getPhoneDisplay(sms.from),
      body: getSmsBody(sms),
      chatName: resolveSmsChatName(sms.from),
      focusMessageUid: sms.uid,
      focusMessageText: getSmsBody(sms),
      target: { type: "sms", phoneNumber: sms.from, companyId: sms.companyId ?? null, sessionNumber: sms.to ?? sessionNumber },
    });
  }, [sessionNumber]);

  useSmsSocket(sessionNumber, companyNumbersData ?? [], handleIncomingSms);

  // MOCK TEMPORAL: Activa este bloque solo para probar localmente el toast de SMS entrante.
  // Quitarlo antes de subir/release, o dejarlo desactivado con shouldMockIncomingSms = false.
  useEffect(() => {
    const shouldMockIncomingSms = false;

    if (!shouldMockIncomingSms || process.env.NODE_ENV !== "development") return;

    const timer = setTimeout(() => {
      handleIncomingSms({
        uid: `mock-sms-${Date.now()}`,
        from: "+17122018195",
        to: sessionNumber || "+10000000000",
        companyId: companyNumbersData?.[0]?.companyId,
        companyName: "Mock Company",
        content: {
          Body: "Hola, este es un mensaje de prueba",
        },
      } as SmsMessageWithCompany);
    }, 2000);

    return () => clearTimeout(timer);
  }, [handleIncomingSms, sessionNumber, companyNumbersData]);

  return null;
}
